// apps/admin-ui/src/features/coin-transactions/table.tsx
import { Link, useNavigate } from '@tanstack/react-router';
import type { CoinTransactionDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { EmptyState } from '../../shared/components/empty-state';
import { formatEnergyDelta } from '../../shared/format-energy';
import type { CoinTransactionsFiltersState } from './filters';
import { ReasonBadge } from './reason-badge';
import { StatusBadge } from './status-badge';

interface Props {
  items: ReadonlyArray<CoinTransactionDto>;
  nextCursor: string | null;
  currentCursor?: string | undefined;
  currentSearch: CoinTransactionsFiltersState & { cursor?: string | undefined; limit?: number | undefined };
}

// Кэш форматтера — Intl.DateTimeFormat не бесплатный.
const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'medium' });

function formatDate(iso: string): string {
  return dateTimeFmt.format(new Date(iso));
}

function userLabel(user: CoinTransactionDto['user']): string {
  const parts: string[] = [];
  if (user.firstName) parts.push(user.firstName);
  if (user.username) parts.push(`@${user.username}`);
  return parts.join(' ') || user.id;
}

function truncate(value: string, max = 16): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

export function CoinTransactionsTable({ items, nextCursor, currentCursor, currentSearch }: Props) {
  const navigate = useNavigate();

  if (items.length === 0 && !currentCursor) {
    return (
      <EmptyState
        title="Транзакций пока нет"
        description="Здесь появятся записи журнала энергии: реферальные бонусы, ручные корректировки и оплаты."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Дата</TableHead>
            <TableHead>Пользователь</TableHead>
            <TableHead className="text-right">Дельта</TableHead>
            <TableHead>Причина</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Idempotency</TableHead>
            <TableHead>Метаданные</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const isPositive = item.delta > 0;
            const isNegative = item.delta < 0;
            const deltaClass = isPositive
              ? 'text-emerald-700 dark:text-emerald-400'
              : isNegative
                ? 'text-rose-700 dark:text-rose-400'
                : 'text-muted-foreground';
            return (
              <TableRow key={item.id}>
                <TableCell className="tabular-nums text-xs whitespace-nowrap">
                  {formatDate(item.createdAt)}
                </TableCell>
                <TableCell className="max-w-[220px] truncate">
                  <Link
                    to="/users/$userId"
                    params={{ userId: item.user.id }}
                    className="hover:underline"
                    title={userLabel(item.user)}
                  >
                    {userLabel(item.user)}
                  </Link>
                </TableCell>
                <TableCell className={`text-right tabular-nums font-semibold ${deltaClass}`}>
                  {formatEnergyDelta(item.delta)}
                </TableCell>
                <TableCell>
                  <ReasonBadge reason={item.reason} />
                </TableCell>
                <TableCell>
                  <StatusBadge status={item.status} />
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground" title={item.idempotencyKey}>
                  {truncate(item.idempotencyKey)}
                </TableCell>
                <TableCell className="max-w-[280px]">
                  {item.metadata && Object.keys(item.metadata).length > 0 ? (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Показать
                      </summary>
                      <pre className="mt-1 max-w-full overflow-x-auto rounded bg-muted p-2 font-mono text-[11px] leading-tight">
                        {JSON.stringify(item.metadata, null, 2)}
                      </pre>
                    </details>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: '/coin-transactions',
                search: () => {
                  const out: Record<string, string | string[]> = {};
                  for (const [k, v] of Object.entries(currentSearch)) {
                    if (k === 'cursor') continue;
                    if (v === undefined || v === null || v === '') continue;
                    if (Array.isArray(v)) {
                      if (v.length === 0) continue;
                      out[k] = v as string[];
                    } else {
                      out[k] = String(v);
                    }
                  }
                  out.cursor = nextCursor;
                  return out;
                },
              })
            }
          >
            Загрузить ещё
          </Button>
        </div>
      )}
    </div>
  );
}
